import withAuth from '../utils/api';
import superagent from 'superagent';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Alert, Card } from 'react-bootstrap';
import type Store from '../../store';
import type { Permission } from '../../models/Permissions';
// The full lockup, not the icon: the login card is the one place the
// product introduces itself, and the white card gives it clear space.
import HGLogo from '../assets/images/hg-logo-blue.svg';
import Spinner from '../components/Spinner';
import { initiateOAuthFlow } from '../utils/oauth';

interface GatewayProps {
  store?: Store;
}

interface IAMResponse {
  connected: boolean;
  tenant?: number;
  /** MadKudu user ObjectId (`mkClaims.userId`), used as the Segment userId. */
  userId?: string;
  email?: string;
  permissions?: Permission[];
  superKudu?: boolean;
  /** RBAC v2 claims forwarded from `mkClaims.rbac` on JWT. Absent for v1. */
  rbac?: import('../../models/Rbac').RbacClaim;
}

type GatewayState =
  | 'loading'
  | 'redirecting-to-oauth'
  | 'redirecting-to-tenant'
  | 'error'
  | 'no-tenant-access';

/**
 * Gateway Component
 *
 * Acts as the entry point for authenticated users. This component:
 * 1. Checks user authentication status via /api/iam
 * 2. If not connected: Redirects to mk-auth OAuth flow
 * 3. If connected with permissions: Redirects to first tenant
 * 4. If connected but no permissions: Shows error message
 * 5. On error: Shows error state with option to retry
 *
 * This component replaces the old LoginPage tenant selection UI.
 * Users never see this component for more than a moment - they're either
 * redirected to OAuth or to their first tenant immediately.
 *
 * @example
 * Rendered at root path (/) in App.tsx
 */
const Gateway = ({ store }: GatewayProps) => {
  const [state, setState] = useState<GatewayState>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function checkAuthAndRedirect() {
      try {
        setState('loading');
        setError(null);

        console.log('[Gateway] Checking authentication status');

        // Fetch IAM data to check authentication and permissions
        const iamResponse = await withAuth(superagent.get('/api/iam'));
        const iamData = iamResponse.body as IAMResponse;

        console.log('[Gateway] IAM response:', {
          connected: iamData.connected,
          email: iamData.email,
          permissions: iamData.permissions?.length || 0,
        });

        // If not connected, redirect to OAuth flow
        if (!iamData.connected) {
          console.log('[Gateway] User not connected, initiating OAuth flow');
          setState('redirecting-to-oauth');

          // Save redirect URL for post-auth redirect (back to /)
          localStorage.setItem('mk_auth_redirect_url', '/');

          // Start OAuth flow
          const callbackUri = `${window.location.origin}/oauth/callback`;
          initiateOAuthFlow(callbackUri);
          return;
        }

        // User is connected - populate store with IAM data
        if (store) {
          store.email = iamData.email || '';
          store.userId = iamData.userId || '';
          store.userPermissions = iamData.permissions || [];
          store.isSuperKudu = iamData.superKudu || false;
          // RBAC v2 (RGI-1042). Absent on iamData for v1 tenants \u2014 null is the
          // v1 bypass signal honored by all consumer-side checks.
          store.rbac = iamData.rbac ?? null;
          store.tenant = iamData.tenant || undefined;
          console.log('[Gateway] Store hydrated:', {
            firstTenant: store.firstTenant,
            isV2User: !!store.rbac,
          });
        }

        // Check if user has any tenant permissions
        if (!iamData.permissions || iamData.permissions.length === 0) {
          console.log('[Gateway] User has no tenant permissions');
          setState('no-tenant-access');
          return;
        }

        // User is connected with permissions - redirect to first tenant
        const firstTenant = store?.firstTenant;
        if (firstTenant) {
          console.log('[Gateway] Redirecting to first tenant:', firstTenant);
          setState('redirecting-to-tenant');
          // Use window.location for hard redirect to ensure fresh page load
          window.location.href = `/tenant/${firstTenant}`;
        } else {
          // Shouldn't happen, but handle gracefully
          console.error('[Gateway] Could not determine first tenant');
          setError('Unable to determine your tenant. Please contact support.');
          setState('error');
        }
      } catch (err) {
        console.error('[Gateway] Error during auth check:', err);

        // Determine error message
        let errorMessage =
          'An error occurred while checking your authentication. ';

        // Check if error has response status (superagent error)
        if (err && typeof err === 'object' && 'status' in err) {
          const status = (err as any).status;
          if (status === 401 || status === 403) {
            errorMessage += 'Your session may have expired. ';
          }
        }

        errorMessage += 'Please try refreshing the page.';

        setError(errorMessage);
        setState('error');
      }
    }

    checkAuthAndRedirect();
  }, [store]);

  // Show loading spinner
  if (state === 'loading') {
    return (
      <div className="hg-login tw-flex tw-h-full tw-items-center tw-justify-center">
        <Card className="tw-min-h-[300px] tw-w-[373px] tw-p-3">
          <Card.Body className="text-start">
            <div className="tw-full tw-mb-4 tw-flex tw-flex-col tw-items-center">
              <div className="tw-my-4">
                <img
                  className="tw-h-9 tw-w-auto"
                  src={HGLogo}
                  alt="HG Insights logo"
                />
              </div>
              <h1 className="fw-bolder tw-mt-2 tw-text-[var(--hg-navy)]">
                Data Studio
              </h1>
            </div>

            <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
              <Spinner />
              <p className="tw-text-center tw-text-[var(--app-muted)]">
                Checking your access...
              </p>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Show redirecting states (brief flash)
  if (state === 'redirecting-to-oauth' || state === 'redirecting-to-tenant') {
    return (
      <div className="hg-login tw-flex tw-h-full tw-items-center tw-justify-center">
        <Card className="tw-min-h-[300px] tw-w-[373px] tw-p-3">
          <Card.Body className="text-start">
            <div className="tw-full tw-mb-4 tw-flex tw-flex-col tw-items-center">
              <div className="tw-my-4">
                <img
                  className="tw-h-9 tw-w-auto"
                  src={HGLogo}
                  alt="HG Insights logo"
                />
              </div>
              <h1 className="fw-bolder tw-mt-2 tw-text-[var(--hg-navy)]">
                Data Studio
              </h1>
            </div>

            <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
              <Spinner />
              <p className="tw-text-center tw-text-[var(--app-muted)]">
                {state === 'redirecting-to-oauth'
                  ? 'Redirecting to sign in...'
                  : 'Redirecting to your workspace...'}
              </p>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Show no tenant access error
  if (state === 'no-tenant-access') {
    return (
      <div className="hg-login tw-flex tw-h-full tw-items-center tw-justify-center">
        <Card className="tw-min-h-[300px] tw-w-[373px] tw-p-3">
          <Card.Body className="text-start">
            <div className="tw-full tw-mb-4 tw-flex tw-flex-col tw-items-center">
              <div className="tw-my-4">
                <img
                  className="tw-h-9 tw-w-auto"
                  src={HGLogo}
                  alt="HG Insights logo"
                />
              </div>
              <h1 className="fw-bolder tw-mt-2 tw-text-[var(--hg-navy)]">
                Data Studio
              </h1>
            </div>

            <Alert variant="warning">
              <Alert.Heading>No Access</Alert.Heading>
              <p className="tw-mb-0">
                You don&apos;t have access to any tenant. Please contact your
                administrator to request access.
              </p>
            </Alert>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Show error state
  if (state === 'error') {
    return (
      <div className="hg-login tw-flex tw-h-full tw-items-center tw-justify-center">
        <Card className="tw-min-h-[300px] tw-w-[373px] tw-p-3">
          <Card.Body className="text-start">
            <div className="tw-full tw-mb-4 tw-flex tw-flex-col tw-items-center">
              <div className="tw-my-4">
                <img
                  className="tw-h-9 tw-w-auto"
                  src={HGLogo}
                  alt="HG Insights logo"
                />
              </div>
              <h1 className="fw-bolder tw-mt-2 tw-text-[var(--hg-navy)]">
                Data Studio
              </h1>
            </div>

            <Alert variant="danger">
              <Alert.Heading>Error</Alert.Heading>
              <p className="tw-mb-0">{error}</p>
            </Alert>

            <div className="tw-mt-4 tw-flex tw-gap-2">
              <button
                className="btn btn-primary tw-flex-1"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return null;
};

export default inject('store')(observer(Gateway));
